import React, { useState, useEffect, memo } from 'react';
import { Activity, X, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { performanceMonitor, PerformanceReport } from '@/utils/performanceMonitor';

const DebugPerformancePanel: React.FC = memo(() => {
  // Development-only guard
  if (!import.meta.env.DEV) return null;

  const [isOpen, setIsOpen] = useState(true); // Always open by default for debugging
  const [metrics, setMetrics] = useState<PerformanceReport | null>(null);

  // Throttled updates via interval
  useEffect(() => {
    const updateMetrics = () => {
      const report = performanceMonitor.generateReport();
      setMetrics(report);
    };

    // Initial load
    updateMetrics();

    // Update every 2 seconds
    const interval = setInterval(updateMetrics, 2000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (avgTime: number, errorRate: number) => {
    if (errorRate > 10 || avgTime > 2000) return 'bg-red-500';
    if (errorRate > 5 || avgTime > 1000) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatDuration = (ms: number) => {
    if (ms > 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  const truncateUrl = (url: string, maxLength = 40) => {
    if (url.length <= maxLength) return url;
    return '...' + url.slice(-maxLength);
  };

  // Get recent network requests (last 20)
  const recentRequests = metrics?.alerts
    ? JSON.parse(sessionStorage.getItem('latest_performance_report') || '{}')?.network
    : null;

  // Get raw network data from sessionStorage
  const rawRequests = (() => {
    try {
      const stored = sessionStorage.getItem('latest_performance_report');
      if (!stored) return [];
      const report = JSON.parse(stored);
      // Access the internal metrics through generateReport
      return [];
    } catch {
      return [];
    }
  })();

  const avgResponseTime = metrics?.network?.averageResponseTime || 0;
  const errorRate = metrics?.network?.errorRate || 0;
  const totalRequests = metrics?.network?.totalRequests || 0;

  return (
    <div className="relative" style={{ contain: 'content' }}>
      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2"
        title="Performance Debug Panel"
      >
        <Activity className="h-5 w-5" />
        {/* Status indicator dot */}
        <span
          className={`absolute top-1 right-1 w-2 h-2 rounded-full ${getStatusColor(avgResponseTime, errorRate)}`}
        />
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              <span className="font-semibold text-sm">Performance Monitor</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="p-1 h-6 w-6">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="text-lg font-bold">{totalRequests}</div>
              <div className="text-xs text-gray-500">Richieste</div>
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${avgResponseTime > 1000 ? 'text-red-600' : avgResponseTime > 500 ? 'text-yellow-600' : 'text-green-600'}`}>
                {formatDuration(avgResponseTime)}
              </div>
              <div className="text-xs text-gray-500">Media</div>
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${errorRate > 5 ? 'text-red-600' : 'text-green-600'}`}>
                {errorRate.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">Errori</div>
            </div>
          </div>

          {/* Errors & Warnings */}
          {metrics && (metrics.errors.total > 0 || metrics.warnings.total > 0) && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-4 text-sm">
                {metrics.errors.total > 0 && (
                  <div className="flex items-center gap-1 text-red-600">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{metrics.errors.total} errori</span>
                  </div>
                )}
                {metrics.warnings.total > 0 && (
                  <div className="flex items-center gap-1 text-yellow-600">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{metrics.warnings.total} warning</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Memory Info */}
          {metrics?.memory && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-xs font-semibold text-gray-500 mb-1">MEMORIA</div>
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  {(metrics.memory.current / 1024 / 1024).toFixed(1)} MB
                </span>
                {metrics.memory.leakSuspected && (
                  <Badge variant="destructive" className="text-xs">
                    Possibile leak
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* API Calls Summary */}
          {metrics?.api && metrics.api.length > 0 && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto">
              <div className="text-xs font-semibold text-gray-500 mb-2">API CALLS</div>
              <div className="space-y-2">
                {metrics.api.slice(0, 10).map((api, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="truncate flex-1 mr-2 text-gray-600 dark:text-gray-400">
                      {truncateUrl(api.url, 30)}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs px-1">
                        x{api.count}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alerts */}
          {metrics?.alerts && metrics.alerts.length > 0 && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 max-h-32 overflow-y-auto bg-orange-50 dark:bg-orange-900/20">
              <div className="text-xs font-semibold text-orange-600 mb-2">ALERTS</div>
              <div className="space-y-1">
                {metrics.alerts.slice(-5).reverse().map((alert, idx) => (
                  <div key={idx} className="text-xs text-orange-700 dark:text-orange-300">
                    {alert.type}: {JSON.stringify(alert.data).slice(0, 50)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Infinite Loops Detection */}
          {metrics?.infiniteLoops && metrics.infiniteLoops.length > 0 && (
            <div className="p-3 bg-red-100 dark:bg-red-900/40">
              <div className="text-xs font-semibold text-red-600 mb-2">INFINITE LOOPS DETECTED</div>
              <div className="space-y-1">
                {metrics.infiniteLoops.map((loop, idx) => (
                  <div key={idx} className="text-xs text-red-700 dark:text-red-300">
                    {loop.pattern.slice(0, 40)}... ({loop.count}x)
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-2 bg-gray-50 dark:bg-gray-800 text-center">
            <span className="text-xs text-gray-500">
              Runtime: {metrics ? metrics.runtime.toFixed(1) : 0} min
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

DebugPerformancePanel.displayName = 'DebugPerformancePanel';

export default DebugPerformancePanel;
